import React, { useEffect, useState, memo, lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import Hero from './components/Hero'
import Navbar from './components/Navbar'
import CinematicShell from './components/CinematicShell'
import CinematicReveal from './components/CinematicReveal'
import SectionTransition from './components/SectionTransition'
import TextMarquee from './components/TextMarquee'
import { SmoothScrollProvider, useSmoothScroll } from './providers/SmoothScrollProvider'
import PrivacyPolicy from './components/PrivacyPolicy'
import TermsOfService from './components/TermsOfService'
import LLM from './components/LLM'
import About from './components/About'
import TerminalWarning from './components/TerminalWarning'
import './App.css'

const Features = lazy(() => import('./components/Features'))
const Showcase = lazy(() => import('./components/Showcase'))
const Contact = lazy(() => import('./components/Contact'))
const GameServers = lazy(() => import('./components/GameServers'))
const Gallery = lazy(() => import('./components/Gallery'))
const Footer = lazy(() => import('./components/Footer'))

const MemoizedHero = memo(Hero)
const MemoizedNavbar = memo(Navbar)

const SectionFallback = () => (
  <div style={{ minHeight: '40vh' }} aria-hidden="true" />
)

const LazyAbout = () => {
  const { refresh } = useSmoothScroll() ?? {}

  useEffect(() => {
    const timer = setTimeout(() => refresh?.(), 150)
    return () => clearTimeout(timer)
  }, [refresh])

  return <About />
}

const LazySection = ({ children }) => {
  const { refresh } = useSmoothScroll() ?? {}

  useEffect(() => {
    const timer = setTimeout(() => refresh?.(), 150)
    return () => clearTimeout(timer)
  }, [refresh])

  return (
    <Suspense fallback={<SectionFallback />}>
      <CinematicReveal>{children}</CinematicReveal>
    </Suspense>
  )
}

const MainContent = () => {
  const [chatOpen, setChatOpen] = useState(false)

  useEffect(() => {
    document.body.className = 'dark'
  }, [])

  useEffect(() => {
    const openChat = () => setChatOpen(true)
    window.addEventListener('open-ai-chat', openChat)
    return () => window.removeEventListener('open-ai-chat', openChat)
  }, [])

  return (
    <SmoothScrollProvider>
      <CinematicShell>
        <div className="app dark">
          <MemoizedNavbar />
          <main>
            <MemoizedHero />

            <div id="about-entry" className="about-entry">
              <SectionTransition index={1} label="About" bridgeId="about" />
              <LazyAbout />
            </div>

            <TextMarquee items={['Web Development', 'FiveM', 'Applications', 'Consulting', 'UI/UX', 'Firebase', 'MYSQL', 'MongoDB', 'Supabase']} />

            <SectionTransition index={2} label="Services" />
            <Suspense fallback={<SectionFallback />}>
              <Features />
            </Suspense>

            <SectionTransition index={3} label="Projects" />
            <LazySection>
              <Showcase />
            </LazySection>

            <SectionTransition index={4} label="Gallery" />
            <LazySection>
              <Gallery />
            </LazySection>

            <SectionTransition index={5} label="Contact" />
            <LazySection>
              <Contact />
            </LazySection>
          </main>

          <Suspense fallback={null}>
            <CinematicReveal>
              <GameServers />
            </CinematicReveal>
            <Footer />
          </Suspense>
        </div>
        <TerminalWarning
          isOpen={chatOpen}
          onToggle={() => setChatOpen((open) => !open)}
        />
      </CinematicShell>
    </SmoothScrollProvider>
  )
}

const App = () => {
  useEffect(() => {
    const googleAnalyticsScript1 = document.createElement('script')
    googleAnalyticsScript1.async = true
    googleAnalyticsScript1.src = 'https://www.googletagmanager.com/gtag/js?id=G-7XSMNM1L6Y'
    document.head.appendChild(googleAnalyticsScript1)

    const googleAnalyticsScript2 = document.createElement('script')
    googleAnalyticsScript2.text = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      window.gtag = gtag;
      gtag('js', new Date());
      gtag('config', 'G-7XSMNM1L6Y');
    `
    document.head.appendChild(googleAnalyticsScript2)

    const cloudflareAnalyticsScript = document.createElement('script')
    cloudflareAnalyticsScript.async = true
    cloudflareAnalyticsScript.src = 'https://static.cloudflareinsights.com/beacon.min.js'
    cloudflareAnalyticsScript.setAttribute('data-cf-beacon', '{"token": "YOUR_CLOUDFLARE_TOKEN_HERE"}')
    document.head.appendChild(cloudflareAnalyticsScript)
  }, [])

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<MainContent />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/llm" element={<LLM />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App
